/*global SwiftWebViewBridge*/
/*global WKWVJBCallbacks*/
/*global WKWebViewJavascriptBridge*/
/*global webkit*/
/*global NativeApp*/

import { ReactLocation } from '@tanstack/react-location';
import { useFlexAssessmentContext } from '../context/context';
import { Device } from '../types/device';
import { iOSBridgeType, IOsHandler } from '../types/native-bridge';
import { PAGE_URLS } from './constants';
import { NativeBridgeConnectionError, NativeMethodError } from './errors';
import { logger } from './logger';

let iOSBridge: iOSBridgeType;

export async function initBridge(
  device: Device,
  location: ReactLocation,
  isOnboarding: boolean
): Promise<void | iOSBridgeType> {
  if (device === Device.iOs) {
    return await initWKWebViewJavascriptBridge();
  } else if (device === Device.android) {
    registerAndroidListeners(location, isOnboarding);
  }
}

function registerAndroidListeners(
  location: ReactLocation,
  isOnboarding: boolean
) {
  const allowExit = !isOnboarding;
  window.stepBack = () => {
    if (location.current.pathname === PAGE_URLS.QUESTIONS) {
      if (allowExit) {
        NativeApp.exitAssessment();
      }
    } else {
      location.history.back();
    }
  };
}

function setupWKWebViewJavascriptBridge(callback: Function): void {
  try {
    if (window.WKWebViewJavascriptBridge) {
      return callback(WKWebViewJavascriptBridge);
    }
    if (window.WKWVJBCallbacks) {
      return window.WKWVJBCallbacks.push(callback);
    }
    window.WKWVJBCallbacks = [callback];
    window.webkit.messageHandlers.iOS_Native_InjectJavascript.postMessage(null);
  } catch (err) {
    logger(err);
    new NativeBridgeConnectionError(Device.iOs);
  }
}

async function initWKWebViewJavascriptBridge(): Promise<iOSBridgeType> {
  /* https://github.com/Lision/WKWebViewJavascriptBridge */
  return new Promise((resolve, reject) => {
    setupWKWebViewJavascriptBridge((bridge: iOSBridgeType): void => {
      iOSBridge = bridge;
      resolve(bridge);
    });
  });
}

export function useNativeBridge(): Record<string, Function> {
  const {
    state: { device },
  } = useFlexAssessmentContext();

  const emitAppBootedEvent = (): void => {
    try {
      if (device === Device.iOs) {
        if (!iOSBridge) {
          throw Error();
        }
        iOSBridge.callHandler(IOsHandler.appBootedSuccessfully, null, null);
      } else if (device === Device.android) {
        NativeApp.appBootedSuccessfully();
      }
    } catch (error) {
      new NativeBridgeConnectionError(device);
      logger(
        `Error booting ${device} DOMContentLoaded at emitAppBootedEvent:`,
        error
      );
    }
  };

  const exitWebview = (): void => {
    try {
      if (device === Device.iOs) {
        iOSBridge.callHandler(IOsHandler.exitAssessment, null, null);
      } else if (device === Device.android) {
        NativeApp.exitAssessment();
      }
    } catch (error) {
      new NativeMethodError(device, IOsHandler.exitAssessment);
      logger(`Error in ${device} exitWebview:`, error);
    }
  };

  const exitScoreDetail = (): void => {
    try {
      if (device === Device.iOs) {
        iOSBridge.callHandler(IOsHandler.exitScoreDetail, null, null);
      } else if (device === Device.android) {
        NativeApp.exitAssessment();
      }
    } catch (error) {
      new NativeMethodError(device, IOsHandler.exitScoreDetail);
      logger(`Error in ${device} exitScoreDetail:`, error);
    }
  };

  const completedAssessment = (score: number): void => {
    const payload = { score };
    try {
      if (device === Device.iOs) {
        iOSBridge.callHandler(IOsHandler.completedAssessment, payload, null);
      } else if (device === Device.android) {
        NativeApp.completedAssessment(JSON.stringify(payload));
      }
    } catch (error) {
      new NativeMethodError(device, IOsHandler.completedAssessment);
      logger(`Error in ${device} completedAssessment:`, error);
    }
  };

  const redirectUrlToNativeBrowser = (url: string): void => {
    try {
      if (device === Device.iOs) {
        iOSBridge.callHandler(
          IOsHandler.redirectUrlToNativeBrowser,
          { url },
          null
        );
      } else if (device === Device.android) {
        NativeApp.redirectUrlToNativeBrowser(url);
      }
    } catch (error) {
      new NativeMethodError(device, IOsHandler.redirectUrlToNativeBrowser);
      logger(`Error in ${device} redirectUrlToNativeBrowser:`, error);
    }
  };

  return {
    emitAppBootedEvent,
    exitWebview,
    exitScoreDetail,
    completedAssessment,
    redirectUrlToNativeBrowser,
  };
}
